
/**
 * WorkflowModeContext - Global state for workflow viewing/editing mode.
 * 
 * Tracks whether workflows are in edit, replay, or debug mode.
 * When a parent workflow is in replay mode, all child workflows opened
 * via tiled windows will automatically inherit replay mode.
 * 
 * This context should be provided at the page level (outside TiledWindowProvider)
 * so all nested WorkflowGraph instances share the same mode.
 */

import { createContext, useContext, useState, useCallback, useMemo, useRef, type ReactNode } from 'react';

/**
 * The current workflow viewing/editing mode.
 * - edit: Normal editing mode (default)
 * - replay: Viewing historical run data
 * - debug: Step-through debugging mode
 */
export type WorkflowMode = 'edit' | 'replay' | 'debug';

/**
 * Context for tracking child workflow runs in replay mode.
 * Maps from "parentRunId:childWorkflowKey" to childRunId.
 */
export interface ChildRunMapping {
    parentRunId: string;
    childWorkflowKey: string;
    childRunId: string;
    stateName: string;
}

interface WorkflowModeContextValue {
    /** Current workflow mode */
    mode: WorkflowMode;
    
    /** The root run ID when in replay mode (the top-level workflow run being replayed) */
    rootReplayRunId: string | null;
    
    /** Debug run ID when in debug mode */
    debugRunId: string | null;
    
    /** Enter replay mode, optionally with a specific run ID */
    enterReplayMode: (runId: string | null) => void;
    
    /** Exit replay mode (returns to edit mode) */
    exitReplayMode: () => void;
    
    /** Enter debug mode with a specific run ID */
    enterDebugMode: (runId: string) => void;
    
    /** Exit debug mode (returns to edit mode) */
    exitDebugMode: () => void;
    
    /** Register a child run mapping (for navigating into child workflows) */
    registerChildRun: (mapping: ChildRunMapping) => void;
    
    /** Get the child run ID for a given parent run and child workflow */
    getChildRunId: (parentRunId: string, childWorkflowKey: string) => string | null;
    
    /** Clear all child run mappings */
    clearChildRuns: () => void;
    
    /** Check if we're in any non-edit mode */
    isLocked: boolean;
}

const WorkflowModeContext = createContext<WorkflowModeContextValue | null>(null);

interface WorkflowModeProviderProps {
    children: ReactNode;
    /** Optional initial mode */
    initialMode?: WorkflowMode;
    /** Optional initial replay run ID */
    initialReplayRunId?: string | null;
}

export function WorkflowModeProvider({
    children,
    initialMode = 'edit',
    initialReplayRunId = null,
}: WorkflowModeProviderProps) {
    const [mode, setMode] = useState<WorkflowMode>(initialMode);
    const [rootReplayRunId, setRootReplayRunId] = useState<string | null>(initialReplayRunId);
    const [debugRunId, setDebugRunId] = useState<string | null>(null);
    
    // Use a ref for child run map to avoid re-renders when registering child runs
    // Registering child runs is a caching operation that shouldn't trigger re-renders
    const childRunMapRef = useRef<Map<string, ChildRunMapping>>(new Map());
    
    const enterReplayMode = useCallback((runId: string | null) => {
        console.log('[WorkflowMode] Entering replay mode with run:', runId);
        setMode('replay');
        if (runId !== null) {
            setRootReplayRunId(runId);
            childRunMapRef.current = new Map(); // Clear child runs when starting new replay
        }
    }, []);
    
    const exitReplayMode = useCallback(() => {
        console.log('[WorkflowMode] Exiting replay mode');
        setMode('edit');
        setRootReplayRunId(null);
        childRunMapRef.current = new Map();
    }, []);
    
    const enterDebugMode = useCallback((runId: string) => {
        console.log('[WorkflowMode] Entering debug mode with run:', runId);
        setMode('debug');
        setDebugRunId(runId);
    }, []);
    
    const exitDebugMode = useCallback(() => {
        console.log('[WorkflowMode] Exiting debug mode');
        setMode('edit');
        setDebugRunId(null);
    }, []);
    
    // Register child run - uses ref to avoid triggering re-renders
    const registerChildRun = useCallback((mapping: ChildRunMapping) => {
        const key = `${mapping.parentRunId}:${mapping.childWorkflowKey}`;
        const existing = childRunMapRef.current.get(key);
        
        // Only log and update if mapping is new or different
        if (!existing || existing.childRunId !== mapping.childRunId) {
            console.log('[WorkflowMode] Registering child run:', { key, childRunId: mapping.childRunId });
            childRunMapRef.current.set(key, mapping);
        }
    }, []);
    
    // Get child run ID - reads from ref, no dependencies needed
    const getChildRunId = useCallback((parentRunId: string, childWorkflowKey: string): string | null => {
        const key = `${parentRunId}:${childWorkflowKey}`;
        const mapping = childRunMapRef.current.get(key);
        return mapping?.childRunId ?? null;
    }, []);
    
    const clearChildRuns = useCallback(() => {
        childRunMapRef.current = new Map();
    }, []);
    
    const isLocked = mode !== 'edit';
    
    // Memoize the context value to prevent unnecessary re-renders of consumers
    const contextValue = useMemo<WorkflowModeContextValue>(() => ({
        mode,
        rootReplayRunId,
        debugRunId,
        enterReplayMode,
        exitReplayMode,
        enterDebugMode,
        exitDebugMode,
        registerChildRun,
        getChildRunId,
        clearChildRuns,
        isLocked,
    }), [
        mode,
        rootReplayRunId,
        debugRunId,
        enterReplayMode,
        exitReplayMode,
        enterDebugMode,
        exitDebugMode,
        registerChildRun,
        getChildRunId,
        clearChildRuns,
        isLocked,
    ]);
    
    return (
        <WorkflowModeContext.Provider value={contextValue}>
            {children}
        </WorkflowModeContext.Provider>
    );
}

/**
 * Hook to access the workflow mode context.
 * Must be used within a WorkflowModeProvider.
 */
export function useWorkflowMode(): WorkflowModeContextValue {
    const context = useContext(WorkflowModeContext);
    if (!context) {
        throw new Error('useWorkflowMode must be used within WorkflowModeProvider');
    }
    return context;
}

/**
 * Hook to check if workflow mode context is available.
 * Returns null if not within a WorkflowModeProvider.
 */
export function useWorkflowModeOptional(): WorkflowModeContextValue | null {
    return useContext(WorkflowModeContext);
}
